'use client'

import { useState, useEffect, useMemo } from 'react'
import {
    ChevronLeft,
    ChevronRight,
    Calendar as CalendarIcon,
    MapPin,
    Clock,
    Plus,
    Trash2,
    X,
    Pencil,
    Image as ImageIcon,
    Video as VideoIcon,
} from 'lucide-react'
//import { useAdmin } from "@/contexts/AdminContext";
import { SubscribeReminderDialog } from '@/components/platform/v2/SubscribeReminderDialog'
import { PageCta, PageHero } from '@/components/platform/v2/PageEdge'
import { Button } from '@/components/platform/v2/ui/button'

const STORAGE_KEY = 'cms_events_list_v1'

type EventItem = {
    id: string
    title: string
    date: string // YYYY-MM-DD
    time: string
    location: string
    shortDescription: string
    fullDescription: string
    cover: string
    mediaUrl: string
    ctaLabel: string
    ctaUrl: string
    tag: string
}

const DEFAULT_EVENTS: EventItem[] = [
    {
        id: 'evt-1',
        title: 'Vcentre.IT на форуме «Бизнес. Технологии. Будущее»',
        date: new Date(Date.now() + 6 * 86400000).toISOString().slice(0, 10),
        time: '10:00',
        location: 'Москва, Экспоцентр',
        shortDescription: 'Презентуем платформу для малого и среднего бизнеса.',
        fullDescription:
            'Команда Vcentre.IT расскажет о том, как объединить CRM, сайт, соцсети и приложение в одном окне. Покажем демо платформы, ответим на вопросы и предоставим участникам форума промокод на бесплатный месяц использования.',
        cover: 'https://images.unsplash.com/photo-1540575467063-178a50c2df87?w=1200&q=80',
        mediaUrl: '',
        ctaLabel: 'Зарегистрироваться',
        ctaUrl: '/auth',
        tag: 'Конференция',
    },
    {
        id: 'evt-2',
        title: 'Мастер-класс: «Воронка продаж в фитнес-клубе»',
        date: new Date(Date.now() + 12 * 86400000).toISOString().slice(0, 10),
        time: '19:00',
        location: 'Онлайн, Zoom',
        shortDescription: 'Разбираем кейс — как клуб увеличил выручку в 2,3 раза.',
        fullDescription:
            'Бесплатный онлайн-мастер-класс для владельцев и управляющих фитнес-клубов. Покажем, как настроить автоматические триггерные сообщения, реферальную систему и квизы для повышения конверсии.',
        cover: 'https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?w=1200&q=80',
        mediaUrl: '',
        ctaLabel: 'Записаться',
        ctaUrl: '/auth',
        tag: 'Вебинар',
    },
    {
        id: 'evt-3',
        title: 'Beauty Tech Day — встреча владельцев салонов',
        date: new Date(Date.now() - 14 * 86400000).toISOString().slice(0, 10),
        time: '12:00',
        location: 'Санкт-Петербург, Loft Hall',
        shortDescription: 'Обсудили автоматизацию beauty-индустрии и новые тренды.',
        fullDescription:
            'Мы провели офлайн-встречу для владельцев салонов красоты. Поделились кейсами, разобрали типичные ошибки в работе с клиентской базой и презентовали новые расширения платформы для бьюти-направления.',
        cover: 'https://images.unsplash.com/photo-1560066984-138dadb4c035?w=1200&q=80',
        mediaUrl: '',
        ctaLabel: 'Смотреть запись',
        ctaUrl: '/auth',
        tag: 'Митап',
    },
]

function loadEvents(): EventItem[] {
    try {
        const raw = localStorage.getItem(STORAGE_KEY)
        if (!raw) return DEFAULT_EVENTS
        const parsed = JSON.parse(raw)
        if (!Array.isArray(parsed) || parsed.length === 0) return DEFAULT_EVENTS
        return parsed
    } catch {
        return DEFAULT_EVENTS
    }
}

function saveEvents(events: EventItem[]) {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(events))
}

const MONTHS = ['Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь', 'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь']
const WEEKDAYS = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс']

function ymd(d: Date) {
    const y = d.getFullYear()
    const m = String(d.getMonth() + 1).padStart(2, '0')
    const day = String(d.getDate()).padStart(2, '0')
    return `${y}-${m}-${day}`
}

const Events = () => {
    //const { isEditing } = useAdmin();
    const [events, setEvents] = useState<EventItem[]>(() => loadEvents())
    const [cursor, setCursor] = useState(() => {
        const d = new Date()
        return new Date(d.getFullYear(), d.getMonth(), 1)
    })
    const [selectedDate, setSelectedDate] = useState<string | null>(null)
    const [openEvent, setOpenEvent] = useState<EventItem | null>(null)
    const [editing, setEditing] = useState<EventItem | null>(null)
    const [subscribeOpen, setSubscribeOpen] = useState(false)

    useEffect(() => {
        saveEvents(events)
    }, [events])

    const eventsByDate = useMemo(() => {
        const map = new Map<string, EventItem[]>()
        for (const e of events) {
            if (!map.has(e.date)) map.set(e.date, [])
            map.get(e.date)!.push(e)
        }
        return map
    }, [events])

    const today = ymd(new Date())

    const calendarDays = useMemo(() => {
        const year = cursor.getFullYear()
        const month = cursor.getMonth()
        const first = new Date(year, month, 1)
        const startWeekday = (first.getDay() + 6) % 7 // Mon=0
        const daysInMonth = new Date(year, month + 1, 0).getDate()
        const cells: { date: Date | null; key: string }[] = []
        for (let i = 0; i < startWeekday; i++) cells.push({ date: null, key: `b-${i}` })
        for (let d = 1; d <= daysInMonth; d++) {
            const date = new Date(year, month, d)
            cells.push({ date, key: ymd(date) })
        }
        while (cells.length % 7 !== 0) cells.push({ date: null, key: `e-${cells.length}` })
        return cells
    }, [cursor])

    const past = useMemo(() => [...events].filter((e) => e.date < today).sort((a, b) => b.date.localeCompare(a.date)), [events, today])

    const dayEvents = selectedDate ? (eventsByDate.get(selectedDate) ?? []) : []

    const addEvent = () => {
        const newEvt: EventItem = {
            id: `evt-${Date.now()}`,
            title: 'Новое мероприятие',
            date: ymd(new Date()),
            time: '12:00',
            location: 'Онлайн',
            shortDescription: 'Краткое описание',
            fullDescription: 'Подробное описание мероприятия...',
            cover: 'https://images.unsplash.com/photo-1505373877841-8d25f7d46678?w=1200&q=80',
            mediaUrl: '',
            ctaLabel: 'Зарегистрироваться',
            ctaUrl: '/auth',
            tag: 'Событие',
        }
        setEvents((prev) => [newEvt, ...prev])
        setEditing(newEvt)
    }

    const updateEvent = (updated: EventItem) => {
        setEvents((prev) => prev.map((e) => (e.id === updated.id ? updated : e)))
    }

    const deleteEvent = (id: string) => {
        if (!confirm('Удалить мероприятие?')) return
        setEvents((prev) => prev.filter((e) => e.id !== id))
        setEditing(null)
        setOpenEvent(null)
    }

    return (
        <div className="vcentre-public pt-24 sm:pt-28 md:pt-32">
            {/* Hero */}
            <PageHero
                eyebrow="События VCENTRE.IT"
                title={<>Мероприятия, в&nbsp;которых мы&nbsp;участвуем</>}
                text={
                    <p>
                        Конференции, вебинары, офлайн-встречи и выставки. Присоединяйтесь — расскажем о платформе, поделимся кейсами и
                        ответим на ваши вопросы.
                    </p>
                }
                actions={
                    <>
                        <Button variant="site" onClick={() => setSubscribeOpen(true)}>
                            <CalendarIcon className="w-4 h-4" />
                            Подписаться на напоминания
                        </Button>
                        {/*isEditing && (
              <Button
                variant="siteOutline"
                onClick={addEvent}
                className="mt-6"
              >
                <Plus className="w-4 h-4" /> Добавить мероприятие
              </Button>
            )*/}
                    </>
                }
                visual={
                    <img
                        src={'/assets/images/platform/events-mascot-hero.png'}
                        alt="Маскот VCENTRE.IT показывает календарь событий"
                        width={754}
                        height={934}
                        loading="eager"
                        className="w-full max-w-[330px] animate-float drop-shadow-[0_30px_60px_rgba(0,0,0,0.6)] sm:max-w-[420px]"
                    />
                }
            />

            {/* Calendar + Day list */}
            <section className="container mx-auto px-4 sm:px-6">
                <div className="grid grid-cols-1 lg:grid-cols-[1.2fr_1fr] gap-8">
                    {/* Calendar */}
                    <div className="bg-card border border-border rounded-2xl p-4 sm:p-6">
                        <div className="flex items-center justify-between mb-6">
                            <button
                                onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1))}
                                className="p-2 rounded-lg hover:bg-muted transition"
                                aria-label="Предыдущий месяц"
                            >
                                <ChevronLeft className="w-5 h-5" />
                            </button>
                            <h2 className="text-lg sm:text-xl font-montserrat-semibold">
                                {MONTHS[cursor.getMonth()]} {cursor.getFullYear()}
                            </h2>
                            <button
                                onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1))}
                                className="p-2 rounded-lg hover:bg-muted transition"
                                aria-label="Следующий месяц"
                            >
                                <ChevronRight className="w-5 h-5" />
                            </button>
                        </div>

                        <div className="grid grid-cols-7 gap-1 mb-2">
                            {WEEKDAYS.map((w) => (
                                <div key={w} className="text-center text-xs font-montserrat text-muted-foreground py-2">
                                    {w}
                                </div>
                            ))}
                        </div>

                        <div className="grid grid-cols-7 gap-1">
                            {calendarDays.map((cell) => {
                                if (!cell.date) return <div key={cell.key} className="aspect-square" />
                                const key = ymd(cell.date)
                                const dayEvts = eventsByDate.get(key) ?? []
                                const isToday = key === today
                                const isSelected = key === selectedDate
                                const isPast = key < today
                                return (
                                    <button
                                        key={cell.key}
                                        onClick={() => setSelectedDate(key)}
                                        className={`aspect-square p-1 sm:p-2 rounded-lg border text-left transition relative
                        ${isSelected ? 'border-primary bg-primary/10' : 'border-transparent hover:border-border hover:bg-muted/50'}
                        ${isPast && !isSelected ? 'opacity-60' : ''}
                      `}
                                    >
                                        <div
                                            className={`text-xs sm:text-sm font-montserrat ${isToday ? 'text-primary font-montserrat-semibold' : ''}`}
                                        >
                                            {cell.date.getDate()}
                                        </div>
                                        {dayEvts.length > 0 && (
                                            <div className="absolute bottom-1 left-1 right-1 flex flex-wrap gap-0.5 justify-start">
                                                {dayEvts.slice(0, 3).map((e, i) => (
                                                    <span
                                                        key={e.id}
                                                        className={`block w-1.5 h-1.5 rounded-full ${isPast ? 'bg-muted-foreground' : 'bg-primary'}`}
                                                    />
                                                ))}
                                                {dayEvts.length > 3 && (
                                                    <span className="text-[9px] text-muted-foreground leading-none">
                                                        +{dayEvts.length - 3}
                                                    </span>
                                                )}
                                            </div>
                                        )}
                                    </button>
                                )
                            })}
                        </div>
                    </div>

                    {/* Day events */}
                    <div className="bg-card border border-border rounded-2xl p-4 sm:p-6">
                        <h3 className="text-lg font-montserrat-semibold mb-4">
                            {selectedDate
                                ? `События на ${new Date(selectedDate).toLocaleDateString('ru-RU', { day: 'numeric', month: 'long', year: 'numeric' })}`
                                : 'Выберите дату в календаре'}
                        </h3>

                        {selectedDate && dayEvents.length === 0 && (
                            <p className="text-sm text-muted-foreground font-montserrat">В этот день мероприятий нет.</p>
                        )}

                        <div className="flex flex-col gap-3">
                            {dayEvents.map((e) => (
                                <button
                                    key={e.id}
                                    onClick={() => setOpenEvent(e)}
                                    className="text-left p-4 rounded-xl border border-border hover:border-primary/50 hover:bg-muted/30 transition group"
                                >
                                    <div className="flex items-center gap-2 mb-2">
                                        <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-primary/10 text-primary font-montserrat-semibold">
                                            {e.tag}
                                        </span>
                                        <span className="text-xs text-muted-foreground font-montserrat flex items-center gap-1">
                                            <Clock className="w-3 h-3" /> {e.time}
                                        </span>
                                    </div>
                                    <h4 className="text-sm sm:text-base font-montserrat-semibold mb-1 group-hover:text-primary transition">
                                        {e.title}
                                    </h4>
                                    <p className="text-xs text-muted-foreground font-montserrat flex items-center gap-1">
                                        <MapPin className="w-3 h-3" /> {e.location}
                                    </p>
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* Past */}
            {past.length > 0 && (
                <section className="container mx-auto px-4 sm:px-6 mt-16 sm:mt-24">
                    <div className="flex items-end justify-between mb-8">
                        <h2 className="text-2xl sm:text-3xl font-montserrat-semibold">Прошедшие мероприятия</h2>
                        <span className="text-sm text-muted-foreground font-montserrat">{past.length} событий</span>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {past.map((e) => (
                            <EventCard
                                key={e.id}
                                event={e}
                                onOpen={() => setOpenEvent(e)}
                                /*onEdit={isEditing ? () => setEditing(e) : undefined}*/
                                past
                            />
                        ))}
                    </div>
                </section>
            )}
            <section className="container-page mt-16 sm:mt-24">
                <PageCta
                    title="Хотите встретиться с командой VCENTRE.ONE?"
                    text="Подпишитесь на напоминания — сообщим о ближайших конференциях, вебинарах и встречах."
                    actions={
                        <Button variant="site" onClick={() => setSubscribeOpen(true)}>
                            Подписаться на события
                        </Button>
                    }
                    visual={
                        <img
                            src={'/assets/images/platform/events-mascot-cta.png'}
                            alt="Маскот VCENTRE.IT с телефоном и фразой «Всегда на связи»"
                            width={768}
                            height={768}
                            loading="lazy"
                            className="w-full max-w-[260px] drop-shadow-[0_24px_48px_rgba(0,0,0,0.55)]"
                        />
                    }
                />
            </section>

            <SubscribeReminderDialog open={subscribeOpen} onOpenChange={setSubscribeOpen} />

            {/* View popup */}
            {openEvent && (
                <EventPopup
                    event={openEvent}
                    onClose={() => setOpenEvent(null)}
                    /*onEdit={isEditing ? () => { setEditing(openEvent); setOpenEvent(null); } : undefined}*/
                />
            )}

            {/* Editor popup */}
            {editing && (
                <EventEditor
                    event={editing}
                    onClose={() => setEditing(null)}
                    onSave={(e) => {
                        updateEvent(e)
                        setEditing(null)
                    }}
                    onDelete={() => deleteEvent(editing.id)}
                />
            )}
        </div>
    )
}

const EventCard = ({ event, onOpen, onEdit, past }: { event: EventItem; onOpen: () => void; onEdit?: () => void; past?: boolean }) => {
    const dateLabel = new Date(event.date).toLocaleDateString('ru-RU', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
    })
    return (
        <div className="group relative bg-card border border-border rounded-2xl overflow-hidden hover:border-primary/50 transition-all">
            <button onClick={onOpen} className="block w-full text-left">
                <div className="aspect-[16/9] overflow-hidden bg-muted relative">
                    <img
                        src={event.cover}
                        alt={event.title}
                        className={`w-full h-full object-cover transition duration-500 group-hover:scale-105 ${past ? 'grayscale group-hover:grayscale-0' : ''}`}
                    />
                    <div className="absolute top-3 left-3 flex items-center gap-2">
                        <span className="text-[10px] uppercase tracking-wider px-2 py-1 rounded-full bg-background/80 backdrop-blur text-foreground font-montserrat-semibold">
                            {event.tag}
                        </span>
                        {past && (
                            <span className="text-[10px] uppercase tracking-wider px-2 py-1 rounded-full bg-muted-foreground/30 text-foreground font-montserrat-semibold">
                                Прошло
                            </span>
                        )}
                    </div>
                </div>
                <div className="p-5">
                    <div className="flex items-center gap-3 text-xs text-muted-foreground font-montserrat mb-3">
                        <span className="flex items-center gap-1">
                            <CalendarIcon className="w-3 h-3" />
                            {dateLabel}
                        </span>
                        <span className="flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {event.time}
                        </span>
                    </div>
                    <h3 className="text-lg font-montserrat-semibold mb-2 group-hover:text-primary transition">{event.title}</h3>
                    <p className="text-sm text-muted-foreground font-montserrat line-clamp-2 mb-3">{event.shortDescription}</p>
                    <p className="text-xs text-muted-foreground font-montserrat flex items-center gap-1">
                        <MapPin className="w-3 h-3" /> {event.location}
                    </p>
                </div>
            </button>
            {onEdit && (
                <button
                    onClick={(e) => {
                        e.stopPropagation()
                        onEdit()
                    }}
                    className="absolute top-3 right-3 p-2 rounded-lg bg-primary text-primary-foreground opacity-0 group-hover:opacity-100 transition"
                    title="Редактировать"
                >
                    <Pencil className="w-4 h-4" />
                </button>
            )}
        </div>
    )
}

const EventPopup = ({ event, onClose, onEdit }: { event: EventItem; onClose: () => void; onEdit?: () => void }) => {
    const dateLabel = new Date(event.date).toLocaleDateString('ru-RU', {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
        year: 'numeric',
    })
    const isExternal = event.ctaUrl.startsWith('http')
    const isPast = event.date < ymd(new Date())
    return (
        <div
            className="fixed inset-0 z-[9998] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto"
            onClick={onClose}
        >
            <div
                className="bg-card border border-border rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto my-auto"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="relative">
                    {event.mediaUrl ? (
                        <div className="aspect-video bg-black">
                            <iframe
                                src={event.mediaUrl}
                                className="w-full h-full"
                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                allowFullScreen
                                title={event.title}
                            />
                        </div>
                    ) : (
                        <div className="aspect-video bg-muted overflow-hidden">
                            <img src={event.cover} alt={event.title} className="w-full h-full object-cover" />
                        </div>
                    )}
                    <button
                        onClick={onClose}
                        className="absolute top-3 right-3 p-2 rounded-full bg-background/80 backdrop-blur hover:bg-background transition"
                        aria-label="Закрыть"
                    >
                        <X className="w-5 h-5" />
                    </button>
                    {onEdit && (
                        <button
                            onClick={onEdit}
                            className="absolute top-3 right-14 p-2 rounded-full bg-primary text-primary-foreground"
                            title="Редактировать"
                        >
                            <Pencil className="w-4 h-4" />
                        </button>
                    )}
                </div>

                <div className="p-6 sm:p-8">
                    <div className="flex flex-wrap items-center gap-2 mb-4">
                        <span className="text-[10px] uppercase tracking-wider px-2 py-1 rounded-full bg-primary/10 text-primary font-montserrat-semibold">
                            {event.tag}
                        </span>
                        <span className="text-xs text-muted-foreground font-montserrat flex items-center gap-1">
                            <CalendarIcon className="w-3 h-3" />
                            {dateLabel}
                        </span>
                        <span className="text-xs text-muted-foreground font-montserrat flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {event.time}
                        </span>
                        <span className="text-xs text-muted-foreground font-montserrat flex items-center gap-1">
                            <MapPin className="w-3 h-3" />
                            {event.location}
                        </span>
                    </div>

                    <h2 className="text-2xl sm:text-3xl font-montserrat-semibold mb-4">{event.title}</h2>
                    <p className="text-base text-muted-foreground font-montserrat whitespace-pre-line leading-relaxed mb-6">
                        {event.fullDescription}
                    </p>

                    {!isPast && (
                        <Button asChild variant="site">
                            <a
                                href={event.ctaUrl}
                                target={isExternal ? '_blank' : undefined}
                                rel={isExternal ? 'noopener noreferrer' : undefined}
                            >
                                {event.ctaLabel}
                            </a>
                        </Button>
                    )}
                </div>
            </div>
        </div>
    )
}

const EventEditor = ({
    event,
    onClose,
    onSave,
    onDelete,
}: {
    event: EventItem
    onClose: () => void
    onSave: (e: EventItem) => void
    onDelete: () => void
}) => {
    const [draft, setDraft] = useState<EventItem>(event)
    const update = <K extends keyof EventItem>(k: K, v: EventItem[K]) => setDraft((d) => ({ ...d, [k]: v }))

    return (
        <div
            className="fixed inset-0 z-[9999] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto"
            onClick={onClose}
        >
            <div
                className="bg-card border border-border rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto my-auto"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="sticky top-0 bg-card border-b border-border p-4 flex items-center justify-between z-10">
                    <h3 className="text-lg font-montserrat-semibold">Редактирование мероприятия</h3>
                    <button onClick={onClose} className="p-2 rounded-lg hover:bg-muted">
                        <X className="w-5 h-5" />
                    </button>
                </div>

                <div className="p-6 flex flex-col gap-4">
                    <Field label="Название">
                        <input className="ev-input" value={draft.title} onChange={(e) => update('title', e.target.value)} />
                    </Field>

                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                        <Field label="Дата">
                            <input type="date" className="ev-input" value={draft.date} onChange={(e) => update('date', e.target.value)} />
                        </Field>
                        <Field label="Время">
                            <input type="time" className="ev-input" value={draft.time} onChange={(e) => update('time', e.target.value)} />
                        </Field>
                        <Field label="Тег">
                            <input className="ev-input" value={draft.tag} onChange={(e) => update('tag', e.target.value)} />
                        </Field>
                    </div>

                    <Field label="Место">
                        <input className="ev-input" value={draft.location} onChange={(e) => update('location', e.target.value)} />
                    </Field>

                    <Field label="Краткое описание">
                        <textarea
                            rows={2}
                            className="ev-input"
                            value={draft.shortDescription}
                            onChange={(e) => update('shortDescription', e.target.value)}
                        />
                    </Field>

                    <Field label="Полное описание">
                        <textarea
                            rows={6}
                            className="ev-input"
                            value={draft.fullDescription}
                            onChange={(e) => update('fullDescription', e.target.value)}
                        />
                    </Field>

                    <Field label="Обложка (URL изображения)" icon={<ImageIcon className="w-4 h-4" />}>
                        <input
                            className="ev-input"
                            value={draft.cover}
                            onChange={(e) => update('cover', e.target.value)}
                            placeholder="https://..."
                        />
                        {draft.cover && <img src={draft.cover} alt="" className="mt-2 rounded-lg max-h-40 object-cover" />}
                    </Field>

                    <Field label="Видео / медиа (embed URL, опционально)" icon={<VideoIcon className="w-4 h-4" />}>
                        <input
                            className="ev-input"
                            value={draft.mediaUrl}
                            onChange={(e) => update('mediaUrl', e.target.value)}
                            placeholder="https://www.youtube.com/embed/..."
                        />
                    </Field>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <Field label="Текст кнопки">
                            <input className="ev-input" value={draft.ctaLabel} onChange={(e) => update('ctaLabel', e.target.value)} />
                        </Field>
                        <Field label="Ссылка кнопки">
                            <input
                                className="ev-input"
                                value={draft.ctaUrl}
                                onChange={(e) => update('ctaUrl', e.target.value)}
                                placeholder="/auth или https://..."
                            />
                        </Field>
                    </div>
                </div>

                <div className="sticky bottom-0 bg-card border-t border-border p-4 flex items-center justify-between gap-3">
                    <Button variant="destructive" onClick={onDelete}>
                        <Trash2 className="w-4 h-4" /> Удалить
                    </Button>
                    <div className="flex gap-2">
                        <Button variant="siteOutline" onClick={onClose}>
                            Отмена
                        </Button>
                        <Button variant="site" onClick={() => onSave(draft)}>
                            Сохранить
                        </Button>
                    </div>
                </div>
            </div>

            <style>{`
        .ev-input {
          width: 100%;
          background: hsl(var(--background));
          border: 1px solid hsl(var(--border));
          border-radius: 0.5rem;
          padding: 0.5rem 0.75rem;
          color: hsl(var(--foreground));
          font-size: 0.875rem;
          font-family: inherit;
        }
        .ev-input:focus {
          outline: none;
          border-color: hsl(var(--primary));
          box-shadow: 0 0 0 2px hsl(var(--primary) / 0.2);
        }
      `}</style>
        </div>
    )
}

const Field = ({ label, children, icon }: { label: string; children: React.ReactNode; icon?: React.ReactNode }) => (
    <label className="flex flex-col gap-1.5">
        <span className="text-xs font-montserrat-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-1.5">
            {icon}
            {label}
        </span>
        {children}
    </label>
)

export default Events
